import { useStripe } from "@stripe/react-stripe-js";
import type { PaymentRequest } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { LanguageT } from "../../../../../../context/GetLifeContext.context";
import { DOMAIN, ENV } from "../../../../../../constants/Global";

const DOMAIN_TO_COUNTRY: Record<string, string> = {
  es: "ES",
  sql_es: "ES",
  wl_es: "ES",
  fr: "FR",
  it: "IT",
  pt: "PT",
  en: "ES",
};

/**
 * The wallet only authorizes the 0,50 € verification charge that Stripe
 * refunds immediately. The recurring subscription is billed afterwards
 * from backend using the saved payment method, so the wallet sheet must
 * reflect the *actual* amount being authorized in this moment, not the
 * subscription price.
 */
const VERIFICATION_AMOUNT_CENTS = 50;

export interface WalletAvailabilityT {
  isGooglePayAvailable: boolean;
  isApplePayAvailable: boolean;
  paymentRequest: PaymentRequest | null;
}

/**
 * Probe Stripe for wallet availability up-front so the picker can decide
 * which options to show. Link is always disabled at the SDK level via
 * `disableWallets`. Apple Pay and Google Pay availability are returned
 * independently by `canMakePayment()` so each card can be enabled or hidden
 * based on the actual browser/device support.
 *
 * Same `paymentRequest` instance is reused across both wallets — Stripe's
 * PaymentRequestButton automatically renders the correct wallet UI for the
 * browser (AP on Safari, GP on Chrome).
 */
export const useWalletAvailability = (
  totalLabel: string
): WalletAvailabilityT => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);

  useEffect(() => {
    if (!stripe) return;
    const country = DOMAIN_TO_COUNTRY[DOMAIN as LanguageT] ?? "ES";

    const pr = stripe.paymentRequest({
      country,
      currency: "eur",
      total: {
        label: totalLabel,
        amount: VERIFICATION_AMOUNT_CENTS,
      },
      requestPayerName: true,
      disableWallets: ["link"],
    } as any);

    let cancelled = false;
    pr.canMakePayment().then((result) => {
      if (cancelled) return;
      if (ENV !== "PRD") {
        // eslint-disable-next-line no-console
        console.info("[useWalletAvailability] canMakePayment →", result);
      }
      if (result) {
        setPaymentRequest(pr);
        setIsGooglePayAvailable(!!result.googlePay);
        setIsApplePayAvailable(!!result.applePay);
      } else {
        setPaymentRequest(null);
        setIsGooglePayAvailable(false);
        setIsApplePayAvailable(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [stripe, totalLabel]);

  return { isGooglePayAvailable, isApplePayAvailable, paymentRequest };
};
